import React, { useState, useRef, useEffect } from "react";
import { Mic, Stop, Delete, Send } from "@mui/icons-material";

export default function VoiceRecorder({ onSendVoice, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  // Extract duration from the recorded audio blob
  const extractAudioDuration = (blob) => {
    return new Promise((resolve) => {
      try {
        const url = URL.createObjectURL(blob);
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        
        audio.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          const duration = Math.floor(audio.duration || 0);
          resolve(duration);
        };
        
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          console.warn('Could not extract duration from recorded audio');
          resolve(recordingTime); // Fallback to recording time
        };
        
        audio.src = url;
      } catch (error) {
        console.error('Error extracting duration:', error);
        resolve(recordingTime); // Fallback to recording time
      }
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { 
          type: mediaRecorderRef.current.mimeType 
        });
        
        // Extract duration from the audio blob
        const duration = await extractAudioDuration(blob);
        setAudioDuration(duration);
        
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    if (isRecording) {
      stopRecording();
    }
    setAudioBlob(null);
    setAudioDuration(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setRecordingTime(0);
  };

  const sendVoiceMessage = () => {
    if (audioBlob) {
      const extension = mediaRecorderRef.current.mimeType.includes('webm') ? 'webm' : 'mp4';
      const file = new File([audioBlob], `voice_${Date.now()}.${extension}`, {
        type: audioBlob.type
      });
      
      // ✅ Attach duration to the file object
      file.duration = audioDuration;
      
      onSendVoice(file);
      cancelRecording();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (audioBlob && !isRecording) {
    return (
      <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border">
        <audio src={audioUrl} controls className="flex-1 h-10" />
        <div className="text-xs text-gray-500 whitespace-nowrap">
          {formatTime(audioDuration)}
        </div>
        <button
          onClick={cancelRecording}
          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
          title="Delete"
        >
          <Delete fontSize="small" />
        </button>
        <button
          onClick={sendVoiceMessage}
          className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors"
          title="Send"
        >
          <Send fontSize="small" />
        </button>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg border border-red-200">
        <div className="flex items-center gap-2 flex-1">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-red-600">
            Recording... {formatTime(recordingTime)}
          </span>
        </div>
        <button
          onClick={cancelRecording}
          className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
          title="Cancel"
        >
          <Delete fontSize="small" />
        </button>
        <button
          onClick={stopRecording}
          className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
          title="Stop Recording"
        >
          <Stop fontSize="small" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={startRecording}
      className="p-2 text-blue-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title="Record Voice Message"
    >
      <Mic fontSize="small" />
    </button>
  );
}